import { useEffect, useRef, useState, type RefObject } from "react";
import type { MapMouseEvent, MapRef } from "react-map-gl/mapbox";

import {
  BOUNDARY_LINE_HIT_PIXELS,
  clampPointToPolygon,
  findBoundaryEdgeInsertion,
  findVertexIndexAtScreenPoint,
  hasLayerFeaturesNearScreenPoint,
  isPointInOrOnPolygon,
} from "context/PlotMap/boundaryUtils";
import type {
  BoundaryPoint,
  LngLatRing,
  ScreenPoint,
} from "context/PlotMap/geo";

export type BoundaryDragTarget = {
  index: number;
  type: "boundary-vertex";
};

interface UseBoundaryMapInteractionParams {
  addBoundaryPoint: (point: BoundaryPoint) => void;
  boundaryLineLayerId: string;
  boundaryPoints: BoundaryPoint[];
  canCloseBoundary: boolean;
  canDrawBoundaryPoints: boolean;
  canInsertOnEdge: boolean;
  canManipulateVertices: boolean;
  closeBoundary: () => void;
  /** When set, clicks/drags snap to this ring so the draft stays inside it. */
  constrainToRing: LngLatRing | null;
  endBoundaryHistoryGesture: () => void;
  insertBoundaryPoint: (index: number, point: BoundaryPoint) => void;
  mapRef: RefObject<MapRef | null>;
  removeBoundaryPoint: (index: number) => void;
  updateBoundaryPoint: (index: number, point: BoundaryPoint) => void;
}

interface UseBoundaryMapInteractionResult {
  handleContextMenu: (event: MapMouseEvent) => void;
  handleMapClick: (event: MapMouseEvent) => boolean;
  handleMouseDown: (event: MapMouseEvent) => boolean;
  handleMouseMove: (event: MapMouseEvent) => boolean;
  handleMouseUp: () => void;
  isDragging: boolean;
}

const useBoundaryMapInteraction = ({
  addBoundaryPoint,
  boundaryLineLayerId,
  boundaryPoints,
  canCloseBoundary,
  canDrawBoundaryPoints,
  canInsertOnEdge,
  canManipulateVertices,
  closeBoundary,
  constrainToRing,
  endBoundaryHistoryGesture,
  insertBoundaryPoint,
  mapRef,
  removeBoundaryPoint,
  updateBoundaryPoint,
}: UseBoundaryMapInteractionParams): UseBoundaryMapInteractionResult => {
  // Ref + state for the same drag target serve different paths:
  // - Ref: pointer handlers read/write synchronously (including mid-drag field
  //   updates) without waiting on a render or closing over a stale target.
  // - State: re-render for isDragging and effects/UI that depend on drag status
  //   (e.g. window mouseup, overlays).
  const draggingTargetRef = useRef<BoundaryDragTarget | null>(null);
  const [draggingTarget, setDraggingTarget] =
    useState<BoundaryDragTarget | null>(null);

  const beginDrag = (target: BoundaryDragTarget, event: MapMouseEvent) => {
    event.originalEvent.preventDefault();
    draggingTargetRef.current = target;
    setDraggingTarget(target);
    mapRef.current?.getMap().dragPan.disable();
  };

  const endPointerInteraction = () => {
    if (draggingTargetRef.current == null) {
      return;
    }

    draggingTargetRef.current = null;
    setDraggingTarget(null);
    mapRef.current?.getMap().dragPan.enable();
    endBoundaryHistoryGesture();
  };

  // Prefer a ref over useCallback in the window mouseup effect deps.
  // Example: mousedown starts a drag → effect attaches window mouseup; mousemove
  // re-renders and recreates endPointerInteraction. With useCallback in the effect
  // deps, each identity change would remove/re-add the listener mid-drag. The ref
  // keeps one subscription for the drag (deps: draggingTarget) while mouseup still
  // calls the latest endPointerInteraction.
  const endPointerInteractionRef = useRef(endPointerInteraction);
  endPointerInteractionRef.current = endPointerInteraction;

  useEffect(() => {
    if (draggingTarget == null) {
      return;
    }

    const handleWindowMouseUp = () => {
      endPointerInteractionRef.current();
    };

    window.addEventListener("mouseup", handleWindowMouseUp);
    return () => {
      window.removeEventListener("mouseup", handleWindowMouseUp);
    };
  }, [draggingTarget]);

  const constrainPoint = (point: BoundaryPoint): BoundaryPoint =>
    constrainToRing == null
      ? point
      : clampPointToPolygon(point, constrainToRing);

  const getVertexIndexAtScreenPoint = (
    screenPoint: ScreenPoint,
  ): number | null => {
    if (mapRef.current == null) {
      return null;
    }

    return findVertexIndexAtScreenPoint(
      mapRef.current,
      boundaryPoints,
      screenPoint,
    );
  };

  const handleMapClick = (event: MapMouseEvent): boolean => {
    if (draggingTargetRef.current != null) {
      return true;
    }

    const clickPoint: BoundaryPoint = {
      latitude: event.lngLat.lat,
      longitude: event.lngLat.lng,
    };

    if (canInsertOnEdge) {
      if (getVertexIndexAtScreenPoint(event.point) != null) {
        return true;
      }

      const clickedBoundaryLine =
        event.features?.some(
          (feature) => feature.layer?.id === boundaryLineLayerId,
        ) ||
        (mapRef.current != null &&
          hasLayerFeaturesNearScreenPoint(
            mapRef.current,
            boundaryLineLayerId,
            event.point,
            BOUNDARY_LINE_HIT_PIXELS,
          ));
      if (!clickedBoundaryLine) {
        return false;
      }

      const insertion = findBoundaryEdgeInsertion(
        boundaryPoints,
        clickPoint,
        true,
      );
      if (insertion == null) {
        return true;
      }

      insertBoundaryPoint(
        insertion.insertIndex,
        constrainPoint(insertion.point),
      );
      return true;
    }

    if (!canDrawBoundaryPoints) {
      return false;
    }

    const vertexIndex = getVertexIndexAtScreenPoint(event.point);
    if (vertexIndex != null) {
      if (vertexIndex === 0 && canCloseBoundary) {
        closeBoundary();
      }
      return true;
    }

    if (
      constrainToRing != null &&
      !isPointInOrOnPolygon(clickPoint, constrainToRing)
    ) {
      return true;
    }

    addBoundaryPoint(clickPoint);
    return true;
  };

  const handleMouseDown = (event: MapMouseEvent): boolean => {
    if (!canManipulateVertices || event.originalEvent.button !== 0) {
      return false;
    }

    const vertexIndex = getVertexIndexAtScreenPoint(event.point);
    if (vertexIndex == null) {
      return false;
    }

    beginDrag({ index: vertexIndex, type: "boundary-vertex" }, event);
    return true;
  };

  const handleMouseMove = (event: MapMouseEvent): boolean => {
    const target = draggingTargetRef.current;
    if (target == null) {
      return false;
    }

    updateBoundaryPoint(
      target.index,
      constrainPoint({
        latitude: event.lngLat.lat,
        longitude: event.lngLat.lng,
      }),
    );
    return true;
  };

  const handleContextMenu = (event: MapMouseEvent) => {
    if (!canManipulateVertices) {
      return;
    }

    const vertexIndex = getVertexIndexAtScreenPoint(event.point);
    if (vertexIndex == null) {
      return;
    }

    event.originalEvent.preventDefault();
    removeBoundaryPoint(vertexIndex);
  };

  return {
    handleContextMenu,
    handleMapClick,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp: endPointerInteraction,
    isDragging: draggingTarget != null,
  };
};

export default useBoundaryMapInteraction;
